/**
 * Motivos de fechamento (spec 054) — seção da configuração da mesa. Com pelo
 * menos um motivo ATIVO, quem fecha um ticket desta mesa escolhe um deles; sem
 * nenhum, a mesa fecha como sempre. Desativar tira da escolha sem apagar dos
 * tickets que já o têm; excluir só vale para motivo que nenhum ticket usa (a
 * API responde 409 e a tela sugere desativar).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Pencil, Trash2, X } from 'lucide-react';
import {
  DESK_CLOSE_REASON_NAME_MAX,
  type DeskCloseReason,
  type DeskCloseReasonCreateBody,
  type DeskCloseReasonUpdateBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { EmptyState, Spinner } from '@/components/ui/misc';
import { deskCloseReasonsOptions, errorMessage } from '@/features/desks/desks-queries';

export function DeskCloseReasonsSection({ deskId }: { deskId: number }) {
  const queryClient = useQueryClient();
  // a chave fica sob ['erp', 'desks']; o diálogo de fechamento lê por ticket
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['erp', 'desks', 'close-reasons', deskId] });
    void queryClient.invalidateQueries({ queryKey: ['erp', 'tickets'] });
  };
  const reasonsQ = useQuery(deskCloseReasonsOptions(deskId));
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (body: DeskCloseReasonCreateBody) => {
      const res = await api<DeskCloseReason>(`/desks/${deskId}/close-reasons`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await api<DeskCloseReason[]>(`/desks/${deskId}/close-reasons/reorder`, {
        method: 'PUT',
        body: { ids },
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const reasons = reasonsQ.data ?? [];
  const activeCount = reasons.filter((r) => r.active).length;

  const add = (e: FormEvent) => {
    e.preventDefault();
    const value = name.trim();
    if (!value) return;
    setError(null);
    create.mutate(
      { name: value, active: true },
      { onSuccess: () => setName(''), onError: (err) => setError(errorMessage(err)) }
    );
  };

  const move = (index: number, delta: -1 | 1) => {
    const ids = reasons.map((r) => r.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setError(null);
    reorder.mutate(ids, { onError: (err) => setError(errorMessage(err)) });
  };

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold">Motivos de fechamento</h2>
        <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
          {reasons.length}
        </span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {activeCount > 0
          ? 'Quem fecha um ticket desta mesa escolhe um destes motivos.'
          : 'Sem motivo ativo, o ticket desta mesa fecha sem pedir motivo.'}{' '}
        Motivo desativado some da escolha, mas continua nos tickets que já o têm.
      </p>

      {reasonsQ.isPending ? (
        <Spinner className="size-5" />
      ) : reasonsQ.isError ? (
        <p className="text-sm text-destructive">{errorMessage(reasonsQ.error)}</p>
      ) : reasons.length === 0 ? (
        <EmptyState className="h-16" message="Nenhum motivo cadastrado" />
      ) : (
        <ul className="divide-y rounded-md border">
          {reasons.map((r, i) => (
            <CloseReasonRow
              key={r.id}
              deskId={deskId}
              reason={r}
              first={i === 0}
              last={i === reasons.length - 1}
              reordering={reorder.isPending}
              onMove={(delta) => move(i, delta)}
              onChanged={invalidate}
              onError={setError}
            />
          ))}
        </ul>
      )}

      <form onSubmit={add} className="mt-3 flex items-center gap-2">
        <Input
          aria-label="Novo motivo de fechamento"
          className="h-8"
          value={name}
          maxLength={DESK_CLOSE_REASON_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          placeholder="Novo motivo (ex.: Instalação realizada)"
        />
        <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
          Adicionar
        </Button>
      </form>

      {error ? (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function CloseReasonRow({
  deskId,
  reason,
  first,
  last,
  reordering,
  onMove,
  onChanged,
  onError,
}: {
  deskId: number;
  reason: DeskCloseReason;
  first: boolean;
  last: boolean;
  reordering: boolean;
  onMove: (delta: -1 | 1) => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(reason.name);

  const update = useMutation({
    mutationFn: async (body: DeskCloseReasonUpdateBody) => {
      const res = await api<DeskCloseReason>(`/desks/${deskId}/close-reasons/${reason.id}`, {
        method: 'PATCH',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await api<void>(`/desks/${deskId}/close-reasons/${reason.id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: onChanged,
  });

  const save = () => {
    const value = name.trim();
    if (!value || value === reason.name) {
      setEditing(false);
      setName(reason.name);
      return;
    }
    onError(null);
    update.mutate(
      { name: value },
      { onSuccess: () => setEditing(false), onError: (err) => onError(errorMessage(err)) }
    );
  };

  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          aria-label={`Subir ${reason.name}`}
          disabled={first || reordering}
          onClick={() => onMove(-1)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowUp className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Descer ${reason.name}`}
          disabled={last || reordering}
          onClick={() => onMove(1)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowDown className="size-3.5" />
        </button>
      </div>

      {editing ? (
        <form
          className="flex min-w-0 flex-1 items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input
            autoFocus
            aria-label="Nome do motivo"
            className="h-7"
            value={name}
            maxLength={DESK_CLOSE_REASON_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="ghost" size="icon-sm" aria-label="Salvar nome">
            <Check />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Cancelar edição"
            onClick={() => {
              setEditing(false);
              setName(reason.name);
            }}
          >
            <X />
          </Button>
        </form>
      ) : (
        <span
          className={`min-w-0 flex-1 truncate text-sm ${reason.active ? '' : 'text-muted-foreground line-through'}`}
        >
          {reason.name}
        </span>
      )}

      <Switch
        aria-label={reason.active ? `Desativar ${reason.name}` : `Ativar ${reason.name}`}
        checked={reason.active}
        disabled={update.isPending}
        onCheckedChange={(active) => update.mutate({ active }, { onError: (err) => onError(errorMessage(err)) })}
      />
      {!editing ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Renomear ${reason.name}`}
          onClick={() => setEditing(true)}
        >
          <Pencil />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Excluir ${reason.name}`}
        disabled={remove.isPending}
        onClick={() => {
          onError(null);
          remove.mutate(undefined, { onError: (err) => onError(errorMessage(err)) });
        }}
      >
        <Trash2 />
      </Button>
    </li>
  );
}
